// The work itself: projects and the secrets sealed onto them, the knowledge base
// they are read against, the plans scout writes, the tasks those become, and the
// blockers that hold one.
// Split out of `../capability-manifest.ts`, which is now the barrel over this
// directory. A GROUP, never a meaning: a capability belongs to whichever group
// its `REST_ROUTES` neighbours are in, so these are absent from the barrel and a
// caller reads `CAPABILITY_MANIFEST`. `./manifest` is the only place they compose,
// and `capability-manifest.test.ts` proves the five are a PARTITION of it.

import { NO_KB_PAGE_AUTHORING_UI, NO_PLAN_ROLLUP_REST, SECRETS_STAY_REST } from './reasons';
import { type Capability, gap, mcp, na, yes } from './surface';

export const WORK_CAPABILITIES: Readonly<Record<string, Capability>> = {
  // ── projects (repos) ──────────────────────────────────────────────────────
  'project.list': { rest: yes, mcp: mcp('project.list'), ui: yes },
  'project.get': { rest: yes, mcp: mcp('project.get'), ui: yes },
  // The PATCH carries all FIVE repo switches, and MCP reaches them as six
  // actions: `add` SETS `enabled` (the half the tier gate can refuse),
  // `disable` clears it, `set_ai_first` declares-or-withdraws the onboarding
  // stamp, `set_donations_enabled` records the funding consent (which asks no
  // billing question at all), `set_canary` the daily canary opt-in, and
  // `set_public_stats` the owner's consent to publish this repo's
  // agent-authored numbers on `GET /v1/public/oss/:owner/:repo/stats`
  // (#5660, owner ruling 2026-10-09). One capability, six addresses — see the
  // header's resolution note and the `task.reassign` precedent.
  //
  // THE ASYMMETRY THIS ROW USED TO CARRY IS GONE (arc 101 slice 04, #4607).
  // `add` set and nothing cleared, so `PATCH {enabled: false}` was REST-only —
  // a one-way door an agent could walk through and not back. Worse,
  // `TASK_STALL_REMEDY[RepoNotOnboarded]` told an MCP-only caller to
  // `PATCH /v1/repos/:owner/:repo` with `ai_first: true`, which is an HTTP call
  // the surface it was served on cannot make. Both directions now have a verb.
  // The five actions share ONE control (the `setEnabled` closure the route
  // stands on), so the durable rows are written once whichever door asked.
  //
  // Why `add` and `disable` are not one `set_enabled {bool}`: only the ON
  // direction runs the tier gate and the duplicate-name refusal, and a shared
  // boolean would let a caller reach the write without either. The dashboard's
  // "Copy as MCP call" can now print the disable direction
  // (`apps/dashboard/src/lib/repos.ts` `repoEnableCall`, which still refuses it).
  'project.update': {
    rest: yes,
    mcp: mcp(
      'project.add',
      'project.disable',
      'project.set_ai_first',
      'project.set_donations_enabled',
      'project.set_canary',
      'project.set_public_stats',
    ),
    ui: yes,
  },
  // One repo's graded SLO report. The MCP door is the `status` verb's optional
  // `repo` argument (its `repo_health` block, the SAME read and projector), not an
  // address of its own — `status` is where "how is it going" is asked, and one
  // verb is claimed by one row (`account.status`), so this row cannot claim it too.
  'project.health': {
    rest: yes,
    mcp: na(
      'MCP reads the same report as `status {repo}` — its `repo_health` block, one read and one projector — and `account.status` claims that verb',
    ),
    ui: gap('no repo-health panel in the dashboard yet; the body is `repoHealthView`'),
  },
  'project.policy_get': { rest: yes, mcp: mcp('project.policy_get'), ui: yes },
  // The platform-side merge hold (#7011): the Fleet page's Merge Gate button is #6779.
  'project.merge_override_get': {
    rest: yes,
    mcp: mcp('project.merge_override_get'),
    ui: gap('the Fleet page Merge Gate button reads it (#6779)'),
  },
  'project.set_merge_override': {
    rest: yes,
    mcp: mcp('project.set_merge_override'),
    ui: gap('the Fleet page Merge Gate button writes it (#6779)'),
  },
  // Where and how, at scale (#7802): the dashboard's `RepoEnablementPanel`.
  'repo_enablement.list': { rest: yes, mcp: mcp('repo_enablement.list'), ui: yes },
  'repo_enablement.facets': { rest: yes, mcp: mcp('repo_enablement.facets'), ui: yes },
  'repo_enablement.bulk': { rest: yes, mcp: mcp('repo_enablement.bulk'), ui: yes },
  // Squads, admin-named groups of repos (#7010): the settings screen `/account/squads` (#6780).
  'squad.list': {
    rest: yes,
    mcp: mcp('squad.list'),
    ui: yes,
  },
  'squad.create': {
    rest: yes,
    mcp: mcp('squad.create'),
    ui: yes,
  },
  // One REST row, two MCP addresses: PATCH carries a rename, a membership change, or both.
  'squad.update': {
    rest: yes,
    mcp: mcp('squad.rename', 'squad.set_repos'),
    ui: yes,
  },
  'squad.delete': {
    rest: yes,
    mcp: mcp('squad.delete'),
    ui: yes,
  },
  'project.policy_validate': { rest: yes, mcp: mcp('project.policy_validate'), ui: yes },
  'project.overview': { rest: yes, mcp: mcp('project.overview'), ui: yes },
  'project.settings': { rest: yes, mcp: mcp('project.settings'), ui: yes },
  // `POST /v1/repos` and `POST /v1/repos/:o/:r/setup` mount the SAME
  // `ProjectControl` the verb dispatches on, so the two OPTIONAL port methods
  // gate both doors identically: no GitHub App credentials in the pod ⇒ neither
  // the `create` action nor the route exists. `served` here states the
  // capability is on the surface where its backing is, which is what the verb's
  // own consumer-or-absent row has always meant.
  // Both UI doors landed with #1727 / #1728. `ui: yes` states the capability is
  // reachable by a human where its backing is — the same reading the two rows
  // above have always had: on a deployment holding no App credential the route
  // is absent and the dialog renders the API's own refusal, which is the
  // capability being unbacked, not unserved.
  'project.create': { rest: yes, mcp: mcp('project.create'), ui: yes },
  // The door is the REPO WORKSPACE, not the onboarding funnel the old gap text
  // named: the verb is per-repo, the funnel is account-wide, and a repo enabled
  // long after onboarding would never reach a funnel step (#1728).
  'project.setup': { rest: yes, mcp: mcp('project.setup'), ui: yes },
  'project.ask': {
    rest: yes,
    mcp: mcp('repo_ask'),
    ui: gap('no ask-this-repo surface in the dashboard'),
  },
  'project.install_landing': {
    rest: yes,
    mcp: na("GitHub's App Setup URL — a browser landing, not a capability a caller invokes"),
    ui: na('it redirects INTO the SPA; it is the hop before one, not a screen'),
  },
  'project.public_stats': {
    rest: yes,
    mcp: na("anonymous callers aren't principals — the public backer wall takes no credential"),
    ui: yes,
  },

  // ── per-repo sealed secrets + the account vault ───────────────────────────
  'secret.list': { rest: yes, mcp: SECRETS_STAY_REST, ui: yes },
  'secret.set': { rest: yes, mcp: SECRETS_STAY_REST, ui: yes },
  'secret.delete': { rest: yes, mcp: SECRETS_STAY_REST, ui: yes },
  // Served since #1851. It was a REST+UI `gap` for its whole prior life — not
  // because nothing was mounted, but because `GET /v1/repos/:repoId/secrets` was
  // swallowed by the earlier `GET /v1/repos/:owner/:repo` (owner=<uuid>,
  // repo=secrets → 404), so claiming `served` would have been exactly the false
  // verdict this manifest exists to make impossible. The collection now lives at
  // `/v1/repos/:owner/:repo/secrets`, which three segments make unshadowable, and
  // `catalog.test.ts` proves it REACHES its handler through the composed app.
  'project_secret.list': { rest: yes, mcp: SECRETS_STAY_REST, ui: yes },
  'project_secret.set': { rest: yes, mcp: SECRETS_STAY_REST, ui: yes },
  'project_secret.delete': { rest: yes, mcp: SECRETS_STAY_REST, ui: yes },

  // ── knowledge base ────────────────────────────────────────────────────────
  // The three reads are the repo workspace's KB tab (`RepoKbTab`): browse the
  // index, rank it, open a page. It is `read:kb`, which is viewer-grade, so the
  // tab is unconditional in `lib/repoTabs.ts` — every role may read what the
  // agent believes about their repo.
  // `kb index` IS the enumeration: every page's slug, hub-first, up to the vault's
  // own page ceiling (`KB_PAGE_SCOPE_CEILING`). This row read "no enumeration verb"
  // for as long as that listing hid behind `kb_search`'s `mode: "index"` flag.
  'kb.page_list': { rest: yes, mcp: mcp('kb.index'), ui: yes },
  'kb.page_get': { rest: yes, mcp: mcp('kb.get'), ui: yes },
  'kb.search': { rest: yes, mcp: mcp('kb.search'), ui: yes },
  // Was an `na` reading "the wiki write-back is the agent's job, not a REST
  // surface" — a statement about who writes it MOST, filed as though it were a
  // reason the capability could not be served. It is exactly the gap-as-`na` this
  // file exists to stop: nothing about a page write is agent-shaped (no session,
  // no run context, no credential we refuse to move), and a maintainer correcting
  // a wrong page or a system importing existing docs is a first-class caller. The
  // UI answer moved with it: an `na` there would have said a human may author a
  // page over REST but never in the dashboard.
  'kb.page_upsert': {
    rest: yes,
    mcp: mcp('kb.upsert'),
    ui: NO_KB_PAGE_AUTHORING_UI,
  },
  // The per-repo knowledge-base STATUS rollup (`GET /v1/kb/status`): one row per
  // ENABLED repo — its wiki page count, when the wiki was last imported, when it
  // was last consolidated, and whether background knowledge-base work is enabled
  // for it. The Repos page renders it; the fleet rollup no longer carries it.
  //
  // REST-ONLY, and the absence is a DECISION rather than an omission: no MCP verb
  // reads the per-repo KB status. The `status` verb's fleet block is a
  // `FleetSnapshot` with no kb member, and `box fleet_status` answers the roster
  // from its own reads — so an MCP twin would be a second door onto a read
  // nothing asks for over MCP.
  'kb.status': {
    rest: yes,
    mcp: na(
      'no MCP verb reads the per-repo knowledge-base status; the status verb reports the fleet, not each repo’s wiki',
    ),
    ui: yes,
  },

  // ── plans + scout ─────────────────────────────────────────────────────────
  'plan.get': { rest: yes, mcp: mcp('plan.get'), ui: yes },
  // `/fleet/plans` (`routes/Plans.tsx`) — the epics index. Before it, an
  // account's plans were reachable only ONE ID AT A TIME, from whichever task row
  // happened to belong to one, so "which plans do I have" had two machine answers
  // and no human one.
  'plan.list': { rest: yes, mcp: mcp('plan.list'), ui: yes },
  // One repo's tasks and plans as ONE read-only feed (#5572), for a dashboard data
  // source: `task`/`plan`/`pr` each carry a write action, so none can hold
  // `readOnlyHint` and a data source refuses to poll them. REST is owed, not decided
  // against — `GET /v1/tasks` (filter `repo`) and `GET /v1/plans` (`repo`) serve the
  // same rows as two collections, never one feed; a REST poller has no annotation to trip on.
  'activity.list': {
    rest: gap('no `GET /v1/repos/:owner/:repo/activity` feed yet; tasks and plans page separately'),
    mcp: mcp('activity.list'),
    ui: gap('no per-repo task + plan feed in the dashboard; Fleet and Plans list the rows apart'),
  },
  // Per-conversation rollup (#2681) — the FIRST reader of `tasks.conversation_ref`.
  // MCP-only today: the read is task-shape (a list of tasks/PRs/blockers for one
  // `conversation_ref`), and no human surface has a "what did this conversation
  // dispatch" view yet, so the UI row stays `gap()` rather than mis-stating one.
  // The deploy-verdict field states `unknown` when no verdict is recorded — the
  // response-shape contract, not a per-call branch.
  'plan.rollup': {
    rest: NO_PLAN_ROLLUP_REST,
    mcp: mcp('plan.rollup'),
    ui: gap('no human surface renders a per-conversation rollup (#2681)'),
  },
  // The merged answer of a multi-repo scout fan-out (#3359) — one sub-scout per
  // repo, their `scout_context` events folded into one answer with per-repo
  // attribution. Computed on read; it mints nothing and stores nothing.
  //
  // The REST half landed in #3451, and the asymmetry it closed is worth keeping
  // written down: `POST /v1/plans` parses the SAME `PlanStartInputSchema` the verb
  // does, so this surface could already MINT a fan-out and answer its `fanout_id`
  // while nothing here could READ what that id addressed. Both doors now mount one
  // `PlanControl.context` — the merge may not exist twice, since #3359 acceptance 2
  // refuses a downstream reader inferring the collapse.
  'plan.context': {
    rest: yes,
    mcp: mcp('plan.context'),
    ui: gap('no human surface renders a cross-repo scout answer (#3451)'),
  },
  // Stopping the epic (#1898) — the plan-level twin of `task.cancel`, and the one
  // WRITE on an otherwise read-only resource.
  'plan.cancel': {
    rest: yes,
    mcp: mcp('plan.cancel'),
    ui: gap('the epics index cannot stop a plan (#1906)'),
  },
  // PAUSE / RESUME the epic (#6751): its running slices paused at a safe step, its waiting
  // slices held by `plans.paused_at`, and both reversed. Two doors onto ONE `PlanControl.pausing`
  // (`apps/api/src/deps/plan-pause.ts`); the card is #6753.
  'plan.pause': {
    rest: yes,
    mcp: mcp('plan.pause'),
    ui: gap('the Fleet card has no pause control yet (#6753)'),
  },
  'plan.resume': {
    rest: yes,
    mcp: mcp('plan.resume'),
    ui: gap('the Fleet card has no pause control yet (#6753)'),
  },
  // RELEASE a held plan (#2680) — the OTHER write on `plan`, the verb that pairs
  // with `require_approval: true` on `POST /v1/plans`. The flip-and-mint is one
  // transaction (`approveHeldPlanGroups`), so a half-flip is unrepresentable.
  // REST mounts the same port the MCP `plan approve` action does, the same
  // posture `plan.cancel` takes — and the UI gap is the same: the dashboard
  // has no held-plan surface yet.
  'plan.approve': {
    rest: yes,
    mcp: mcp('plan.approve'),
    ui: gap('the dashboard has no held-plan surface (#1906)'),
  },
  // REGROUP — replace a stuck group with the tasks that replace it, so the plan still
  // tracks the slot. REST and MCP mount the same `PlanControl.regroup`; the UI gap is the
  // stop's: the epics index has no per-group control.
  'plan.regroup': {
    rest: yes,
    mcp: mcp('plan.regroup'),
    ui: gap('the epics index has no per-group control (#1906)'),
  },
  // UNLINK — drop an ordering-only upstream edge from one group of an open plan (#5523).
  // REST and MCP mount the same `PlanControl.unlink`; the UI gap is regroup's: the
  // epics index has no per-group control.
  'plan.unlink': {
    rest: yes,
    mcp: mcp('plan.unlink'),
    ui: gap('the epics index has no per-group control (#1906)'),
  },
  // EXECUTE a committed plan directory — the plan is read on the `plan.execute`
  // lane and born as ONE plan whose groups the merged-gate releases; REST and MCP
  // mount the same `PlanControl.execute`. A dry run lands on the held state
  // `plan.approve` releases, so the UI gap is that one's.
  'plan.execute': {
    rest: yes,
    mcp: mcp('plan.execute'),
    ui: gap('the dashboard has no held-plan surface (#1906)'),
  },
  // (#5129) FILE a plan directory inline — the `plan file` door. Both REST
  // (`POST /v1/plans/file`) and MCP mount the same `PlanControl.file`, so the
  // capability row mirrors `plan.execute`'s shape — REST + MCP, no UI surface
  // (the dashboard has no plan-file composer; the chat client does that work).
  'plan.file': {
    rest: yes,
    mcp: mcp('plan.file'),
    ui: gap('no dashboard surface files a plan yet'),
  },
  // The epic's model (#7012): REST (`PUT /v1/plans/:planId/model`) and MCP mount the same
  // `PlanControl.setModel` (`apps/api/src/deps/plan-model.ts`). The human surface is the
  // epic row's model picker (#6753), which has not shipped yet.
  'plan.set_model': {
    rest: yes,
    mcp: mcp('plan.set_model'),
    ui: gap('the epic row has no model picker yet (#6753)'),
  },
  // The per-org plan-file switch (#5107): REST and MCP mount the same
  // `PlanControl.fileSwitchGet` / `fileSwitchSet`; both doors are served, the
  // owner flips it with a credential (or the dashboard will, when it grows the
  // settings row the epic's UI slice owes).
  'plan.file_switch_get': {
    rest: yes,
    mcp: mcp('plan.file_switch_get'),
    ui: gap('no dashboard control for the plan-file switch yet'),
  },
  'plan.file_switch_set': {
    rest: yes,
    mcp: mcp('plan.file_switch_set'),
    ui: gap('no dashboard control for the plan-file switch yet'),
  },
  // This row was once the one whose REST/UI gaps were NOT owed work anybody could
  // pick up: `plan_start` was catalogued with no `PlanStarter` in `apps/api`, so the
  // verb answered `-32601 tool not found` in prod and a second door would have
  // doubled a published-and-absent defect rather than closed it. The port landed
  // (`apps/api/src/deps/plan-start.ts`) and `POST /v1/plans` now mounts that same
  // object, so the REST half is served and only the UI gap is left.
  //
  // ONE CONTRACT NOTE, since it shapes what the UI renders: a start answers a TASK
  // id, not a run id. The run id is minted on the box at claim time, so
  // `tasks.run_id` is null until a box starts the session — the REST door publishes
  // that same null-until-started shape (as `GET /v1/tasks/:id` does), and
  // `<StartPlanResult/>` renders it as dispatched-then-claimed rather than inventing
  // a live run to point at.
  'plan.start': {
    rest: yes,
    mcp: mcp('plan_start'),
    // `/fleet/plans` now carries the start control (`<StartPlanModal/>`), which
    // fires the REST door the dashboard can actually reach. Three surfaces, one
    // `PlanStarter`.
    ui: yes,
  },

  // ── asks (the admission gate's caller-facing door) ────────────────────────
  //
  // The RAW TASK, filed by a caller — the object triage decides on, which
  // `task.create` below is not (that mints one executable task and runs no
  // triage). `docs/idea/lifecycle.md` § 1 names the API inlet and
  // `RawTaskSource.Api` declares it; until this pair nothing wrote it, and 0 of
  // the 485 production `raw_tasks` rows came from a caller (2026-09-11).
  //
  // The UI gap is real and named rather than filed as `na`: a human composing an
  // ask in the dashboard is a surface somebody will want, and the only reason it
  // is absent is that the caller this door was built for is an AI holding a
  // token. `next_steps` is the nearest thing a human has today.
  //
  // THE OPTIONAL `issue` FIELD, on BOTH doors (`rest` and `mcp` alike): a caller
  // may DECLARE the GitHub issue this ask is about — `{repo, number}` — and the
  // platform then GUARANTEES three things, or refuses the ask before it writes
  // anything:
  //   - the issue was READ through the account's own GitHub App installation, so
  //     the stored reference names an issue that exists (`issue_not_found`
  //     otherwise — and an issue the platform could not read is never stored as
  //     if it had);
  //   - it is OPEN and it is not a pull request (`issue_closed` /
  //     `issue_is_pull_request`), because a closed issue has nothing left to
  //     close and a pull request shares the numbering space without being an
  //     issue;
  //   - it lives in one of the repos THIS ask names (`issue_repo_not_in_ask`),
  //     because `Closes #n` closes an issue in the PR's OWN repository.
  // A mention in the ask's PROSE links nothing: the number that closes an issue
  // is a fact the platform confirms, never text a model read.
  'ask.file': {
    rest: yes,
    mcp: mcp('ask.file'),
    ui: gap('no dashboard composer for a raw ask; a human dispatches through the task board'),
  },
  'ask.get': {
    rest: yes,
    mcp: mcp('ask.get'),
    ui: gap(
      'no dashboard view of a filed ask or its triage verdict; the board shows the tasks it minted, never the ask',
    ),
  },
  'ask.redrive': {
    rest: yes,
    mcp: mcp('ask.redrive'),
    ui: gap(
      'no dashboard re-drive of a `judge_unavailable` escalation; an operator re-queues it through a token, and the board shows only the tasks a re-driven ask mints',
    ),
  },

  // ── tasks ─────────────────────────────────────────────────────────────────
  'task.create': {
    rest: yes,
    mcp: na(
      'new work enters MCP as `ask file`, which triage cuts into tasks; a verb that minted one directly would be a door around the gate',
    ),
    ui: yes,
  },
  // arc 101 slice 06 — dispatching a DEVOPS run. MCP-only on purpose, and both
  // absences are decisions rather than backlog: a REST twin would be a second door
  // onto the one surface that mints work for the role that reaches production, and
  // the dashboard's own answer to "run a devops task" is the task board it already
  // has. Add either the day someone needs it, not before.
  //
  // `mcp: served` means the verb is IN THE CATALOG, which it is. It is not yet MOUNTED
  // in production: `blockerTool`-style consumer-or-absent registration means the tool
  // appears only when `ports.ops` is supplied, and `apps/api` has no `deps/ops.ts` yet.
  // Raised in review (#2343) as "the manifest advertises what production cannot
  // execute", and the distinction is worth keeping straight rather than papering over:
  // this file describes the CODE's surface, and every consumer of it (the parity test,
  // the generated docs) reads it that way. The runtime gap is one composition-root
  // object; it is named in the PR body and owed before anyone can call the verb.
  'ops.dispatch': {
    rest: gap(
      'no REST twin yet — the verb landed on MCP first and a second door onto devops dispatch is not free to add',
    ),
    mcp: mcp('ops.dispatch'),
    ui: gap(
      'no dashboard control yet; a devops run appears on the existing task board once dispatched',
    ),
  },
  // #2963 — the ops verb that schedules the `.dz/` migration consumer. MCP
  // only (no REST twin this slice); the consumer itself lives at
  // `apps/worker/src/consumers/dz-config-migrate.ts` and drains the
  // `dz.config_migrate` BullMQ lane. DEFAULTS TO DRY-RUN: `confirm: true` is
  // the only shape that opens PRs. Addressed `dz_migrate.migrate` since plan
  // 2026-10-01 row 10-17 moved it from a flat tool into the army registry
  // (`packages/mcp/src/army/dz-migrate.ts` `dzMigrateVerb`); the capability id
  // on the left is unchanged.
  dz_migrate: {
    rest: gap(
      'no REST twin yet — the verb landed on MCP first, scheduling one `dz.config_migrate` BullMQ job per named repo (or per `--all` resolved via the #2961 inventory)',
    ),
    mcp: mcp('dz_migrate.migrate'),
    ui: gap('no dashboard control yet; an operator runs it from MCP until the migration completes'),
  },
  'task.list': { rest: yes, mcp: mcp('task.list'), ui: yes },
  'task.run_status': { rest: yes, mcp: mcp('task.run_status'), ui: yes },
  // The run timeline ships in TWO shapes, and they are two capabilities for the
  // same reason `session.events` and `session.events_export` are: a stream a
  // client subscribes to and a page a client polls are not one answer in two
  // envelopes. The SSE leg keeps this id and its path; the MCP verb's claim sits
  // on the PAGED row below, which is the shape it actually serves.
  'task.run_tail': {
    rest: yes,
    mcp: na(
      'an SSE tail is not a JSON-RPC shape; `task.run_tail` polls the same rows and is filed on `task.run_events` (the `session.events` precedent)',
    ),
    ui: yes,
  },
  // Keyed on the TASK, not the run, deliberately: `run_id` is null until a box
  // claims and starts the work, so a run-keyed poll would 404 on work that has
  // simply not begun.
  'task.run_events': { rest: yes, mcp: mcp('task.run_tail'), ui: yes },
  // The per-task attempt LADDER (#2760) — a capability of its own rather than a
  // field on `task.run_status`, and for the reason the two rows above are two:
  // every run-keyed surface can only ever show the CURRENT run, because
  // `tasks.run_id` is overwritten by each retry. This is the one read that
  // answers what happened on attempts 1 and 2 of 3.
  // WHAT A RUN DELIVERED (#3553 F3): the report body with its source, the raw tasks
  // a devops run filed, the blocker it declared, the devops summary, and a
  // `return_context` scout's survey (#4269) — folded off the
  // task's `task_run_events`. That last arm is the whole answer for a SCOUT rather than
  // one field more: that lane writes no `report` row, so until it was named this read
  // served a delivered scout the view of a task no box had started.
  // Its own row beside `task.run_events` because a caller
  // that dispatched a report / devops run wants the answer, not the rows the answer
  // is spread across; `task.run_events` stays the raw timeline.
  'task.result': {
    // `GET /v1/tasks/:id/report` (arc 101 slice 06) — the REST twin over the SAME
    // `TaskControl.result`, so the fold is decided once and the two doors cannot
    // answer differently. The dashboard is the caller that closed the gap: the
    // devops report is rendered on the run page, and paging
    // `GET /v1/tasks/:id/events` to reassemble it would make every consumer
    // re-implement `projectTaskResult`.
    rest: yes,
    mcp: mcp('task.result'),
    // `ui: yes` carries no address — the type says so — so the evidence goes here.
    // `routes/DevopsReport.tsx` at `/fleet/tasks/:task_id/report` renders the
    // markdown through the sanitize seam; the way IN is the "Run report" action on
    // `routes/FleetAudit.tsx`, the run timeline that the fleet queue, task detail,
    // the attempt ladder, review runs and the repo scorecard all already link to.
    //
    // This row was a `gap` for one release because the page existed with nothing
    // linking into it, and the distinction was worth keeping: a row claiming a
    // surface a reader cannot locate is the same defect as denying one that exists.
    //
    // The link is gated on the task id the stream resolves off its first frame
    // (`task_id` is non-null on every projected row), NOT on a report existing —
    // and that second half is the null-report contract paying off: `GET
    // /v1/tasks/:id/report` answers 200 with `report: null` for a task that has
    // delivered nothing yet, which the view names in its own words, so the link
    // never needs a second read to avoid pointing at nothing.
    ui: yes,
  },
  // "IS IT DONE?" in ONE call (#3631): the task's status, its current run, the PR
  // with CI / review verdicts, the deploy proof and the open blocker, folded under
  // one `phase` — what used to take `task run_status` -> `task result` -> `pr list`
  // -> `status`. Same read scope as `task.run_status`; one SQL statement
  // (`@developerz/db` `getTaskOutcome`) behind the port.
  'task.outcome': {
    rest: gap(
      'no REST twin yet — the fold landed on MCP first, where the four-call loop it replaces was measured; `GET /v1/tasks/:id` still answers the row and the pulse separately',
    ),
    mcp: mcp('task.outcome'),
    ui: gap('no dashboard view yet; the task detail page shows the same facts across its tabs'),
  },
  'task.attempts': {
    rest: yes,
    mcp: gap(
      'no verb yet — an agent polling `task run_status` sees the live attempt, and the ladder is a post-hoc read a human does; add one when an agent needs to reason over past attempts',
    ),
    ui: yes,
  },
  // The stop control (#1898). Two doors onto ONE `TaskControl.cancel`, so the
  // account fence, the `pr_review` carve-out, the interrupt and the audit row are
  // decided once. The UI half is #1906 (the fleet views' stop button), which is
  // why the gap names the ticket rather than an opinion.
  'task.cancel': {
    rest: yes,
    mcp: mcp('task.cancel'),
    ui: gap('the fleet views show running work but cannot stop it (#1906)'),
  },
  // PAUSE / RESUME one job (#6751): stop at a safe step, keep the machine, continue from the
  // saved work. Two doors onto ONE `TaskControl.pausing` (`apps/api/src/deps/task-pause.ts`), so
  // the lane gate, the verbs gate, the flood bound and the audit rows are decided once.
  'task.pause': {
    rest: yes,
    mcp: mcp('task.pause'),
    ui: gap('the Fleet card has no pause control yet (#6753)'),
  },
  'task.resume': {
    rest: yes,
    mcp: mcp('task.resume'),
    ui: gap('the Fleet card has no pause control yet (#6753)'),
  },
  // WHY IS NOTHING MOVING (arc 101 slice 11 Part C) — one call, three doors, one
  // answer. It is a capability of its own rather than a field on `task.list`:
  // the list answers "what work exists", this answers "what is holding it", and
  // a queue can be deep for a reason nothing in the list carries.
  'task.stalls': {
    rest: yes,
    mcp: mcp('task.stalls'),
    ui: gap('the fleet views show a deep queue but not the reasons holding it (#2117)'),
  },
  // ── blockers (the answerable hold) ────────────────────────────────────────
  //
  // `mcp: served` on these three is now true of PRODUCTION and not only of the
  // code. It was neither until #2421: `armyTools` registers `blockerTool` only
  // `if (ports.blocker)` and `apps/api` had no `deps/blockers.ts`, so the verb
  // was unreachable by any client while this file said `served`. That gap is the
  // one `ops.dispatch` above still carries, and it is worth keeping the two
  // straight rather than papering over either.
  'blocker.list': {
    rest: yes,
    mcp: mcp('blocker.list'),
    ui: yes,
  },
  'blocker.answer': {
    rest: yes,
    mcp: mcp('blocker.answer'),
    ui: yes,
  },
  'blocker.withdraw': {
    rest: yes,
    mcp: mcp('blocker.withdraw'),
    ui: yes,
  },
  // THE MACHINE PATH (arc 101 slice 03, epic #3756): the form a `missing_credential`
  // blocker asks to be filled, read and filled by NAME. One control behind every
  // door (`apps/api/src/deps/blocker-submit.ts`): these two REST routes, the MCP
  // actions, and the fix-link page below. `submit` gates on `write:secrets` on
  // both transports — it writes vault values, and that is the scope every other
  // vault write takes.
  'blocker.spec': {
    rest: yes,
    mcp: mcp('blocker.spec'),
    ui: na(
      'a human reads the same form on the fix-link page (`blocker.fix_link_form`), which needs no login; this is the machine path onto it',
    ),
  },
  'blocker.submit': {
    rest: yes,
    mcp: mcp('blocker.submit'),
    ui: na(
      'a human fills the same form on the fix-link page (`blocker.fix_link_redeem`), which needs no login; this is the machine path onto it',
    ),
  },
  // THE FIX LINK — a signed capability to fill ONE blocker's form, served from the
  // API origin as a page. REST-only on every axis because it is a PAGE for a
  // human: the token-authorized way to do the same thing is `blocker.spec` /
  // `blocker.submit` above, on REST and MCP alike. Partial fills, write-once per
  // field, and the link stays valid for its hour while fields remain.
  'blocker.fix_link_form': {
    rest: yes,
    mcp: na(
      'the form is for a HUMAN with a browser and no login; an agent reads the same form with `blocker spec`, or hands a person the `fix_url` from `blocker list`',
    ),
    ui: na(
      'it renders from the API origin, so it works for someone who has never signed in — an SPA route would need a session the recipient may not have',
    ),
  },
  'blocker.fix_link_redeem': {
    rest: yes,
    mcp: na(
      'the link is the credential of a human with no login; an agent holding a token fills the same form with `blocker submit`, which is this capability on MCP',
    ),
    ui: gap(
      'the dashboard has a repo Secrets tab but no blocker-driven form; a signed-in maintainer stores values there, or opens the fix link',
    ),
  },
  'task.reassign': {
    rest: yes,
    // Two doors onto ONE `TaskControl.reassign`: the REST route and the resource
    // action. The flat `task_reassign` duplicate (#1089) is deleted, not aliased.
    mcp: mcp('task.reassign'),
    ui: gap('the queue board shows assignment but cannot change it'),
  },
  // The operator re-drive (#4023): a terminal (or otherwise stuck) task back to
  // `pending`, refusing a task already `pending` or one an operator `cancelled` on
  // purpose. Two doors onto ONE `TaskControl.redispatch` since #4235, which also
  // releases the dependents that failed because the task did.
  'task.redispatch': {
    rest: yes,
    mcp: mcp('task.redispatch'),
    ui: gap('the fleet views show a failed task but have no re-drive action yet (follow-up)'),
  },
  // Order an EXISTING task behind other existing tasks — the repair for an order
  // triage or a plan never wrote (the ask-time twin is `ask file {after_tasks}`).
  'task.depend': {
    rest: gap('no REST route yet; the MCP action is the one door onto `TaskControl.depend`'),
    mcp: mcp('task.depend'),
    ui: gap('the fleet views cannot add an ordering edge to a queued task'),
  },
};
